import * as React from "react";
import {useRef, useState} from "react";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {userApi} from "@/boxhub-api";
import {isOperatorRole, useAuth} from "@/app/auth/AuthContext";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {AppFrame} from "@/app/AppFrame";
import {useModelState} from "@/lib/modelstate";
import {Box, CircularProgress, FormGroup, Grid2 as Grid, Typography} from "@mui/material";
import {CompanyMembershipStatus, UserActions, UserDTO, UserSettingKey} from "@/api/data-contracts";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {AlternateEmail, Delete, Lock, LockOpen, Send} from "@mui/icons-material";
import {UserStatusBadge} from "@/common/UserStatusBadge";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {Constants} from "@/api/constants";
import InfoTooltipIcon from "@/lib/components/InfoTooltipIcon";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import {CardEx} from "@/lib/components/CardEx";
import {useUserEditSchema} from "@/pages/user-page/schema";
import {toLanguageSelectOptions} from "@/pages/user-page/languageSelectOptions";
import {languageOptions, salutationOptions, useAvailableLanguages} from "@/datacaches";
import {changeAppLanguage} from "@/app/locale/i18nBundle";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {useTranslation} from "react-i18next";
import {AdminI18N, CommonI18N, UserI18N} from "@/translations";
import {LinkDisplayDialog} from "@/common/dialogs/LinkDisplayDialog";
import {truncateString} from "@/lib/utils";
import {AppPaths} from "@/app/AppPaths";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {useSnackbarEx} from "@/lib/snackbarex";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import {onClose, useDialogController} from "@/lib/dialog-controller";
import {UserTokenDialog} from "@/pages/user-page/UserTokenDialog";
import {GroupBox} from "@/lib/components/GroupBox";
import {
    EMAIL_NOTIFICATION_KEYS,
    MAX_DISTANCE_KM_KEY,
    toBool,
    toIntOrNull,
    toStr
} from "@/pages/user-page/notification-mapping";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import NumberInputField from "@/lib/components/inputfields/number-input-field";
import {InvitationInfoCard} from "@/common/InvitationInfoCard";

interface EmailChangeDialogProps {
    open: boolean;
    close: () => void;
    fullName: string;
    onConfirm: (newEmail: string) => void;
}

const EmailChangeDialog = ({open, close, fullName, onConfirm}: EmailChangeDialogProps) => {
    const {t} = useTranslation();
    const [newEmail, setNewEmail] = React.useState("");

    const handleConfirm = () => {
        const emailToSubmit = newEmail;
        close();
        setTimeout(() => onConfirm(emailToSubmit), 320);
    };

    const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setNewEmail(e.target.value);
    };

    return (
            <ConfirmationDialog
                    open={open}
                    onClose={close}
                    onConfirm={handleConfirm}
                    title={t(AdminI18N.users.dialog.changeEmailTitle)}
                    message={t(AdminI18N.users.dialog.changeEmailMessage, {
                        fullName,
                        newEmail: newEmail || "…"
                    })}
                    confirmButtonText={t(CommonI18N.message.yes)}
                    cancelButtonText={t(CommonI18N.message.no)}
                    confirmDisabled={!newEmail.trim()}
            >
                <Box sx={{mt: 2}}>
                    <StringInputField
                            name="newEmail"
                            label={t(UserI18N.form.field.email)}
                            value={newEmail}
                            onChange={handleEmailChange}
                            maxChars={Constants.EMAIL_LENGTH_MAX}
                            type="email"
                    />
                </Box>
            </ConfirmationDialog>
    );
};

const isNotifStateDirty = (
        current: Record<UserSettingKey, boolean>,
        baseline: Record<UserSettingKey, boolean> | null
) => {
    if (!baseline) {
        return true;
    }
    for (const key of EMAIL_NOTIFICATION_KEYS) {
        if (current[key] !== baseline[key]) {
            return true;
        }
    }
    return false;
};

const UserPage = () => {
    const {t} = useTranslation();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const companyIdFromQuery = searchParams.get("companyId") ? Number(searchParams.get("companyId")) : undefined;
    const navigate = useNavigate();
    const {user, company, otherCompanyMemberships, roles} = useAuth();
    const {showMessage} = useSnackbarEx();

    const salutationSelectOptions = salutationOptions.map((option) => ({
        label: option && t(CommonI18N.enum.salutation[option]),
        value: option
    }));

    const [availableLanguages] = useAvailableLanguages();
    // While `useAvailableLanguages()` is loading (or permanently, on a fetch error) it resolves
    // to `[]` — fall back to the static baseline so the dropdown isn't empty and the schema's
    // `.oneOf(...)` (see schema.ts) never validates against an empty set.
    const effectiveLanguages = availableLanguages.length > 0 ? availableLanguages : languageOptions;
    const languageSelectOptions = toLanguageSelectOptions(effectiveLanguages, t);

    const [locked, setLocked] = useState(true);
    const [DialogNode, showDialog] = useDialogController();
    const [linkDialogData, setLinkDialogData] = useState<
            { title: string; url: string | undefined; validUntil?: string; subtitle?: string } | undefined
    >(undefined);

    const createEmptyNotifState = React.useCallback(
            () =>
                    EMAIL_NOTIFICATION_KEYS.reduce(
                            (acc, key) => {
                                acc[key] = false;
                                return acc;
                            },
                            {} as Record<UserSettingKey, boolean>
                    ),
            []
    );

    const [mailNotifs, setMailNotifs] = useState<Record<UserSettingKey, boolean>>(() => createEmptyNotifState());
    const [initialMailNotifs, setInitialMailNotifs] = useState<Record<UserSettingKey, boolean> | null>(null);
    const [maxDistanceKm, setMaxDistanceKm] = useState<number | null>(null);
    const [initialMaxDistanceKm, setInitialMaxDistanceKm] = useState<number | null>(null);
    /** Ref that always holds the latest maxDistanceKm — avoids stale closures in functional state updaters. */
    const maxDistanceKmRef = useRef<number | null>(null);
    const [notifDirty, setNotifDirty] = useState(false);
    const [notifSaving, setNotifSaving] = useState(false);

    const schema = useUserEditSchema(effectiveLanguages);

    const {model, errors, onChange, status, isDirty, dispatch, setErrors, save, load, httpErrorStatus} = useModelState<
            UserDTO,
            number
    >({
        initialState: {} as UserDTO,
        id: +params.id!,
        schema,
        loader: (id) => userApi.getUser(companyIdFromQuery!, id),
        updater: userApi.saveUser,
        validateDisabled: locked
    });

    const settingsCompanyId = companyIdFromQuery ?? company?.id ?? model?.company?.id;
    const iAmTheUser = user?.id === model?.id;
    const selectedCompanyId = companyIdFromQuery ?? model?.company?.id;
    const canDeleteUser = model?.allowedActions?.includes(UserActions.DELETE) ?? false;
    const canResetPassword = model?.allowedActions?.includes(UserActions.PASSWORD_RESET) ?? false;
    const canResendInvitation = model?.allowedActions?.includes(UserActions.RESEND_INVITATION) ?? false;
    const canChangeEmail = model?.allowedActions?.includes(UserActions.CHANGE_EMAIL) ?? false;
    const canGenerateUserSignupLink = model?.allowedActions?.includes(UserActions.GENERATE_USER_SIGNUP_LINK) ?? false;
    const canGeneratePasswordResetLink = model?.allowedActions?.includes(UserActions.GENERATE_PASSWORD_RESET_LINK) ?? false;
    const canShowInvitationCard =
        isOperatorRole(roles) &&
        (model?.status === CompanyMembershipStatus.INVITED ||
            model?.status === CompanyMembershipStatus.INVITED_LOCKED) &&
        !!model?.invitation;

    const doSaveUser = async () => {
        try {
            const promise = save();
            if (promise) {
                const response = await promise;
                setLocked(true);
                // Apply the newly saved language immediately — only for the own profile; saving
                // another user's profile must never change the acting admin's own display language.
                const savedLanguage = (response.data as UserDTO | undefined)?.language;
                if (iAmTheUser && savedLanguage) {
                    await changeAppLanguage(savedLanguage);
                }
            }
        } catch (error: unknown) {
            showMessage({summary: t(CommonI18N.message.saveFailed), severity: "error", error});
        }
    };

    const pageTitle = (
            <>
                {`${t(UserI18N.form.field.profile)} '${truncateString(model?.firstName, 32)} ${truncateString(model?.lastName, 32)}'`}{" "}
                <UserStatusBadge status={model?.status}/>
            </>
    );

    const confirmLockForm = () => {
        if (isDirty) {
            dispatch({type: "--reset"});
        }
        setLocked(true);
        setErrors({});
    };

    const showLockConfirmDialog = () => {
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={onClose(close, confirmLockForm)}
                        title={t(UserI18N.form.dialog.lockTitle)}
                        message={t(UserI18N.form.dialog.lockMessage)}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    const handleLockToggle = () => {
        if (locked) {
            setLocked(false);
        } else if (isDirty) {
            showLockConfirmDialog();
        } else {
            setLocked(true);
            setErrors({});
        }
    };

    const handleDeleteUser = () => {
        const fullName = `${model?.firstName ?? ""} ${model?.lastName ?? ""}`.trim();

        const confirmDeleteUser = async () => {
            if (!model?.id) {
                return;
            }
            try {
                const companyId = companyIdFromQuery ?? model?.company?.id;
                if (companyId) {
                    await userApi.deleteUser(companyId, model.id);
                }
                showMessage({summary: t(AdminI18N.users.message.deleteSuccess), severity: "success"});
                if (companyId) {
                    navigate(AppPaths.COMPANY_fn(companyId), {replace: true});
                }
            } catch (error: unknown) {
                showMessage({summary: t(CommonI18N.message.saveFailed), severity: "error", error});
            }
        };

        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={onClose(close, confirmDeleteUser)}
                        title={t(AdminI18N.users.dialog.deleteTitle)}
                        message={t(AdminI18N.users.dialog.deleteMessage, {fullName})}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    const handlePasswordReset = () => {
        const fullName = `${model?.firstName ?? ""} ${model?.lastName ?? ""}`.trim();

        const confirmPasswordReset = async () => {
            if (!model?.id) {
                return;
            }
            try {
                await userApi.triggerPasswordReset(model.id);
                showMessage({summary: t(AdminI18N.users.message.passwordResetSuccess), severity: "success"});
            } catch (error: unknown) {
                showMessage({summary: t(CommonI18N.message.saveFailed), severity: "error", error});
            }
        };

        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={onClose(close, confirmPasswordReset)}
                        title={t(AdminI18N.users.dialog.passwordResetTitle)}
                        message={t(AdminI18N.users.dialog.passwordResetMessage, {fullName})}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    const handleResendInvitation = () => {
        const fullName = `${model?.firstName ?? ""} ${model?.lastName ?? ""}`.trim();

        const confirmResendInvitation = async () => {
            if (!model?.id || !selectedCompanyId) {
                return;
            }

            try {
                await userApi.resendUserInvitation(model.id, selectedCompanyId);
                showMessage({summary: t(AdminI18N.users.message.resendInvitationSuccess), severity: "success"});
                load();
            } catch (error: unknown) {
                showMessage({summary: t(AdminI18N.users.message.resendInvitationFailed), severity: "error", error});
            }
        };

        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={onClose(close, confirmResendInvitation)}
                        title={t(AdminI18N.users.dialog.resendInvitationTitle)}
                        message={t(AdminI18N.users.dialog.resendInvitationMessage, {fullName})}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    const handleChangeEmail = () => {
        const fullName = `${model?.firstName ?? ""} ${model?.lastName ?? ""}`.trim();

        const confirmChangeEmail = async (newEmail: string) => {
            if (!model?.id) {
                return;
            }
            try {
                const response = await userApi.changeEmail(model.id, {email: newEmail});
                dispatch({type: "--set", data: response.data});
                showMessage({summary: t(AdminI18N.users.message.changeEmailSuccess), severity: "success"});
            } catch (error: unknown) {
                showMessage({summary: t(AdminI18N.users.message.changeEmailFailed), severity: "error", error});
            }
        };

        showDialog((open, close) => (
                <EmailChangeDialog
                        open={open}
                        close={close}
                        fullName={fullName}
                        onConfirm={confirmChangeEmail}
                />
        ));
    };

    const handleGenerateUserSignupLink = () => {
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={async () => {
                            close();
                            if (!model?.id || !model?.membershipId) {
                                showMessage({
                                    summary: t(AdminI18N.generateUserSignupLink.message.errorNotFound),
                                    severity: "error"
                                });
                                return;
                            }
                            try {
                                const response = await userApi.generateUserSignupLink(model.id, model.membershipId);
                                const {url, validUntil} = response.data;
                                setLinkDialogData({
                                    title: t(AdminI18N.linkDisplay.dialog.userSignupTitle),
                                    url,
                                    validUntil: validUntil ?? undefined,
                                    subtitle: t(AdminI18N.linkDisplay.message.warning)
                                });
                                load();
                            } catch (error: unknown) {
                                const axiosError = error as { response?: { status?: number } };
                                if (axiosError?.response?.status === 404) {
                                    showMessage({
                                        summary: t(AdminI18N.generateUserSignupLink.message.errorNotFound),
                                        severity: "error"
                                    });
                                } else if (axiosError?.response?.status === 409) {
                                    showMessage({
                                        summary: t(AdminI18N.generateUserSignupLink.message.errorConflict),
                                        severity: "error"
                                    });
                                } else {
                                    showMessage({
                                        summary: t(AdminI18N.generateUserSignupLink.message.errorConflict),
                                        error,
                                        severity: "error"
                                    });
                                }
                            }
                        }}
                        title={t(AdminI18N.generateUserSignupLink.dialog.confirmTitle)}
                        message={t(AdminI18N.generateUserSignupLink.dialog.confirmMessage)}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    const handleGeneratePasswordResetLink = () => {
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={async () => {
                            close();
                            if (!model?.id) {
                                showMessage({
                                    summary: t(AdminI18N.generatePasswordResetLink.message.errorNotFound),
                                    severity: "error"
                                });
                                return;
                            }
                            try {
                                const response = await userApi.generatePasswordResetLink(model.id);
                                const {url, validUntil} = response.data;
                                setLinkDialogData({
                                    title: t(AdminI18N.linkDisplay.dialog.passwordResetTitle),
                                    url,
                                    validUntil: validUntil ?? undefined,
                                    subtitle: t(AdminI18N.linkDisplay.message.warning)
                                });
                            } catch (error: unknown) {
                                const axiosError = error as { response?: { status?: number } };
                                if (axiosError?.response?.status === 404) {
                                    showMessage({
                                        summary: t(AdminI18N.generatePasswordResetLink.message.errorNotFound),
                                        severity: "error"
                                    });
                                } else if (axiosError?.response?.status === 409) {
                                    showMessage({
                                        summary: t(AdminI18N.generatePasswordResetLink.message.errorConflict),
                                        severity: "error"
                                    });
                                } else {
                                    showMessage({
                                        summary: t(AdminI18N.generatePasswordResetLink.message.errorConflict),
                                        error,
                                        severity: "error"
                                    });
                                }
                            }
                        }}
                        title={t(AdminI18N.generatePasswordResetLink.dialog.confirmTitle)}
                        message={t(AdminI18N.generatePasswordResetLink.dialog.confirmMessage)}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    const handleNewToken = (expirationDate: string | undefined) => {
        dispatch({type: "--change", data: ["apiTokenExpirationDate", expirationDate], onChange: true});
    };

    const confirmGenerateToken = async () => {
        try {
            const response = await userApi.createApiToken();
            showDialog((open, close) => (
                    <UserTokenDialog open={open} close={close} token={response.data} handleToken={handleNewToken}/>
            ));
        } catch (error: unknown) {
            showMessage({summary: t(UserI18N.form.message.generatedApiTokenFailed), severity: "error", error});
        }
    };

    const showAskNewTokenDialog = () => {
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={onClose(close, confirmGenerateToken)}
                        title={t(UserI18N.form.dialog.generatedApiTokenTitle)}
                        message={t(UserI18N.form.dialog.generatedApiTokenMessage)}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    const handleNotifCheckboxChange =
            (key: UserSettingKey) =>
                    ({value}: { name: string; value: boolean }) => {
                        const checked = Boolean(value);
                        // Read the latest maxDistanceKm via ref to avoid stale-closure issues
                        // inside the functional state updater.
                        const currentMaxDistance = maxDistanceKmRef.current;
                        setMailNotifs((prev) => {
                            const next = {...prev, [key]: checked};
                            setNotifDirty(isNotifStateDirty(next, initialMailNotifs) || currentMaxDistance !== initialMaxDistanceKm);
                            return next;
                        });
                    };

    const handleMaxDistanceKmChange = ({value}: { name: string; value: number | null | undefined }) => {
        const next = value ?? null;
        maxDistanceKmRef.current = next;
        setMaxDistanceKm(next);
        setNotifDirty(
                isNotifStateDirty(mailNotifs, initialMailNotifs) || next !== initialMaxDistanceKm
        );
    };

    const handleSaveNotifications = async (): Promise<boolean> => {
        if (!model?.id || !settingsCompanyId) {
            return false;
        }
        // Validate max-distance range before saving
        const mainSwitchOn = mailNotifs[UserSettingKey.EMAIL_NOTIFICATIONS_NEW_REQUEST_PUBLISHED];
        if (mainSwitchOn && maxDistanceKm !== null) {
            const isValidDistance =
                    Number.isInteger(maxDistanceKm) &&
                    maxDistanceKm >= Constants.MAX_DISTANCE_KM_MIN &&
                    maxDistanceKm <= Constants.MAX_DISTANCE_KM_MAX;
            if (!isValidDistance) {
                showMessage({
                    summary: t(UserI18N.notifications.validation.maxDistanceKmInvalid, {
                        min: Constants.MAX_DISTANCE_KM_MIN,
                        max: Constants.MAX_DISTANCE_KM_MAX
                    }),
                    severity: "error"
                });
                return false;
            }
        }
        setNotifSaving(true);
        try {
            const settings: Record<UserSettingKey, string> = {} as Record<UserSettingKey, string>;
            for (const key of EMAIL_NOTIFICATION_KEYS) {
                settings[key] = toStr(mailNotifs[key]);
            }
            // Include max-distance setting: send "" when main switch is off or value is not set
            settings[MAX_DISTANCE_KM_KEY] = mainSwitchOn && maxDistanceKm !== null ? String(maxDistanceKm) : "";
            await userApi.putAll(model.id, settingsCompanyId, {settings});
            setInitialMailNotifs(mailNotifs);
            setInitialMaxDistanceKm(maxDistanceKm);
            setNotifDirty(false);
            showMessage({summary: t(CommonI18N.message.saved), severity: "success"});
            return true;
        } catch (error: unknown) {
            showMessage({summary: t(CommonI18N.message.saveFailed), severity: "error", error});
            return false;
        } finally {
            setNotifSaving(false);
        }
    };

    const hasOtherActiveMemberships = iAmTheUser && (otherCompanyMemberships?.length ?? 0) > 0;

    const handleCopyEmailSettings = () => {
        if (!model?.id || !settingsCompanyId) {
            return;
        }
        const targetCompanies = (otherCompanyMemberships ?? []).filter((c) => (c.name ?? "").length > 0);

        const confirmCopy = async () => {
            if (!model?.id || !settingsCompanyId) {
                return;
            }
            const saved = await handleSaveNotifications();
            if (!saved) {
                return;
            }
            try {
                const response = await userApi.copyEmailSettingsToOtherMemberships(model.id, settingsCompanyId);
                const count = response.data.updatedCompanies?.length ?? 0;
                showMessage({
                    summary: t(UserI18N.notifications.message.copyCombinedSuccess, {count}),
                    severity: "success"
                });
            } catch (error: unknown) {
                showMessage({summary: t(CommonI18N.message.saveFailed), severity: "error", error});
            }
        };

        const safeConfirmCopy = () => {
            confirmCopy().catch((error: unknown) => {
                showMessage({summary: t(CommonI18N.message.saveFailed), severity: "error", error});
            });
        };

        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={onClose(close, safeConfirmCopy)}
                        title={t(UserI18N.notifications.dialog.copyTitle)}
                        message={
                            <>
                                <span>{t(UserI18N.notifications.dialog.copyContent)}</span>
                                <ul>
                                    {targetCompanies.map((c) => (
                                            <li key={c.id}>{c.name}</li>
                                    ))}
                                </ul>
                                <span>{t(UserI18N.notifications.dialog.copyWarning)}</span>
                            </>
                        }
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                />
        ));
    };

    React.useEffect(() => {
        const userId = model?.id;
        if (!userId || !iAmTheUser || !settingsCompanyId) {
            return;
        }
        const loadSettings = async () => {
            try {
                const {data} = await userApi.getAllSettings(userId, settingsCompanyId);
                const effective = data?.effective ?? {};
                const next = createEmptyNotifState();
                for (const [key, value] of Object.entries(effective)) {
                    const typedKey = key as UserSettingKey;
                    if (EMAIL_NOTIFICATION_KEYS.includes(typedKey)) {
                        next[typedKey] = toBool(value);
                    }
                }
                // Load max-distance setting (INTEGER, not boolean)
                const rawMaxDistance = effective[MAX_DISTANCE_KM_KEY];
                const maxDistanceValue = toIntOrNull(rawMaxDistance);
                maxDistanceKmRef.current = maxDistanceValue;
                setMailNotifs(next);
                setInitialMailNotifs(next);
                setMaxDistanceKm(maxDistanceValue);
                setInitialMaxDistanceKm(maxDistanceValue);
                setNotifDirty(false);
            } catch (error: unknown) {
                showMessage({summary: t(CommonI18N.message.loadFailed), severity: "error", error});
            }
        };
        void loadSettings();
    }, [model?.id, iAmTheUser, settingsCompanyId, createEmptyNotifState, showMessage, t]);

    const buttons = (
            <Grid container spacing={2}>
                {/* Delete button top-right, visible for CEOs/admins; not rendered for self-delete */}
                <Grid>
                    <ButtonEx
                            visible={canDeleteUser}
                            label={t(AdminI18N.users.action.delete)}
                            StartIcon={Delete}
                            color="error"
                            variant="contained"
                            onClick={handleDeleteUser}
                            data-cy="delete-user-button"
                    />
                </Grid>
                <Grid>
                    <ButtonEx
                            visible={canResetPassword}
                            label={t(AdminI18N.users.action.triggerPasswordReset)}
                            color="warning"
                            variant="contained"
                            onClick={handlePasswordReset}
                            data-cy="reset-password-button"
                    />
                </Grid>
                <Grid>
                    <ButtonEx
                            visible={canResendInvitation}
                            label={t(AdminI18N.users.action.resendInvitation)}
                            color="info"
                            variant="contained"
                            StartIcon={Send}
                            onClick={handleResendInvitation}
                            disabled={status === "saving" || !selectedCompanyId || !model?.id}
                            data-cy="resend-invitation-button"
                    />
                </Grid>
                <Grid>
                    <ButtonEx
                            visible={canChangeEmail}
                            label={t(AdminI18N.users.action.changeEmail)}
                            color="info"
                            variant="contained"
                            StartIcon={AlternateEmail}
                            onClick={handleChangeEmail}
                            disabled={status === "saving" || !model?.id}
                            data-cy="change-email-button"
                    />
                </Grid>
                <Grid>
                    <ButtonEx
                            visible={canGenerateUserSignupLink}
                            label={t(AdminI18N.generateUserSignupLink.action.generate)}
                            color="secondary"
                            variant="contained"
                            onClick={handleGenerateUserSignupLink}
                            disabled={status === "saving" || !model?.id || !model?.membershipId}
                            data-cy="generate-user-signup-link-button"
                    />
                </Grid>
                <Grid>
                    <ButtonEx
                            visible={canGeneratePasswordResetLink}
                            label={t(AdminI18N.generatePasswordResetLink.action.generate)}
                            color="secondary"
                            variant="contained"
                            onClick={handleGeneratePasswordResetLink}
                            disabled={status === "saving" || !model?.id}
                            data-cy="generate-password-reset-link-button"
                    />
                </Grid>

                {!locked && (
                        <Grid>
                            <ButtonEx
                                    label={
                                        status === "saving" ? (
                                                <CircularProgress size={24}/>
                                        ) : (
                                                t(AdminI18N.companies.action.saveChanges)
                                        )
                                    }
                                    data-cy="save-user-button"
                                    onClick={doSaveUser}
                                    color="primary"
                                    disabled={!isDirty || status === "saving"}
                            />
                        </Grid>
                )}
                {iAmTheUser && (
                        <Grid>
                            <ButtonEx
                                    label={t(UserI18N.form.action.generateNewApiToken)}
                                    data-cy={"generate-api-token-button"}
                                    onClick={showAskNewTokenDialog}
                                    color="primary"
                            />
                        </Grid>
                )}
                {iAmTheUser && (
                        <Grid>
                            <IconButtonEx
                                    Icon={locked ? Lock : LockOpen}
                                    tooltip={
                                        locked
                                                ? t(AdminI18N.companies.action.unlockForEditing)
                                                : t(AdminI18N.companies.action.lockEditing)
                                    }
                                    onClick={handleLockToggle}
                                    disabled={status === "saving"}
                            />
                        </Grid>
                )}
            </Grid>
    );

    return (
            <AppFrame
                    modelState={status}
                    httpErrorStatus={httpErrorStatus}
                    title={pageTitle}
                    buttons={buttons}
                    isDirty={isDirty}
            >
                <Grid container spacing={2} paddingTop={1}>
                    <Grid size={{xs: 12}}>
                        <CardEx title={t(UserI18N.form.page.title)}>
                            <Grid container spacing={3} direction="column" width="100%">
                                <Grid container direction="row" spacing={2} width="100%">
                                    <Grid size={{sm: 6, md: 4, lg: 3}}>
                                        <StringInputField
                                                name="email"
                                                disabled
                                                value={model?.email}
                                                label={t(UserI18N.form.field.email)}
                                                error={errors?.email}
                                                maxChars={Constants.EMAIL_LENGTH_MAX}
                                                endAdornment={
                                                        !canChangeEmail && (
                                                                <InfoTooltipIcon
                                                                        tooltipText={t(UserI18N.form.message.emailCannotBeChanged)}
                                                                />
                                                        )
                                                }
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} width="100%">
                                    <Grid size={{sm: 4, md: 2, lg: 3}}>
                                        <SelectInputField
                                                name="salutation"
                                                disabled={locked}
                                                label={t(UserI18N.form.field.salutation)}
                                                value={model?.salutation}
                                                error={errors?.salutation}
                                                options={salutationSelectOptions}
                                                displayEmpty
                                                onChange={onChange}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} width="100%">
                                    <Grid size={{sm: 6, md: 4, lg: 3}}>
                                        <StringInputField
                                                name="firstName"
                                                disabled={locked}
                                                label={t(UserI18N.form.field.firstName)}
                                                value={model?.firstName}
                                                error={errors?.firstName}
                                                maxChars={Constants.FIRSTNAME_LENGTH_MAX}
                                                onChange={onChange}
                                        />
                                    </Grid>
                                    <Grid size={{sm: 6, md: 4, lg: 3}}>
                                        <StringInputField
                                                name="lastName"
                                                disabled={locked}
                                                label={t(UserI18N.form.field.lastName)}
                                                value={model?.lastName}
                                                error={errors?.lastName}
                                                maxChars={Constants.LASTNAME_LENGTH_MAX}
                                                onChange={onChange}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} width="100%">
                                    <Grid size={{sm: 6, md: 4, lg: 3}}>
                                        <StringInputField
                                                name="telephone"
                                                disabled={locked}
                                                label={t(UserI18N.form.field.phone)}
                                                value={model?.telephone}
                                                error={errors?.telephone}
                                                maxChars={Constants.PHONE_NUMBER_LENGTH_MAX}
                                                onChange={onChange}
                                        />
                                    </Grid>
                                    <Grid size={{sm: 6, md: 4, lg: 3}}>
                                        <StringInputField
                                                name="fax"
                                                disabled={locked}
                                                label={t(UserI18N.form.field.fax)}
                                                value={model?.fax}
                                                error={errors?.fax}
                                                maxChars={Constants.PHONE_NUMBER_LENGTH_MAX}
                                                onChange={onChange}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} width="100%">
                                    <Grid size={{sm: 6, md: 4, lg: 3}}>
                                        <SelectInputField
                                                name="language"
                                                disabled={locked}
                                                label={t(UserI18N.form.field.language)}
                                                value={model?.language}
                                                error={errors?.language}
                                                options={languageSelectOptions}
                                                displayEmpty
                                                onChange={onChange}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardEx>
                    </Grid>
                    {canShowInvitationCard && (
                            <Grid size={{xs: 12}}>
                                <InvitationInfoCard invitation={model?.invitation}/>
                            </Grid>
                    )}
                    {iAmTheUser && (
                            <Grid size={{xs: 12}}>
                                <CardEx title={t(UserI18N.form.field.apiToken)}>
                                    <Grid container spacing={2} direction="column" width="100%">
                                        <Grid size={{xs: 12, sm: 6, md: 4}}>
                                            {model?.apiTokenExpirationDate ? (
                                                    <DateInputField
                                                            name="apiTokenExpirationDate"
                                                            disabled
                                                            value={model?.apiTokenExpirationDate}
                                                            label={t(UserI18N.form.field.apiTokenExpirationDate)}
                                                    />
                                            ) : (
                                                    <Typography variant="body2" color="textSecondary" component="div">
                                                        {t(UserI18N.form.message.noApiToken)}
                                                    </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </CardEx>
                            </Grid>
                    )}
                    {iAmTheUser && (
                            <Grid size={{xs: 12}}>
                                <CardEx title={t(UserI18N.notifications.page.title)}>
                                    <Grid container spacing={3}>
                                        {/* Requester Section - Left Column */}
                                        <Grid size={{xs: 12, lg: 6}}>
                                            <Grid container direction="column" spacing={3}>
                                                <Typography variant="h6" gutterBottom>
                                                    {t(UserI18N.notifications.section.requester)}
                                                </Typography>

                                                <GroupBox title={t(UserI18N.notifications.subgroup.eventBased)}>
                                                    <FormGroup>
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_PUBLISHED_ME}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_PUBLISHED_ME
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_PUBLISHED_ME
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestPublishedMe)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_PUBLISHED_COLLEAGUES
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_REQUEST_PUBLISHED_COLLEAGUES
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_PUBLISHED_COLLEAGUES
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestPublishedColleagues)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_ME}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_ME
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_ME
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestCancelledMe)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_COLLEAGUES
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_COLLEAGUES
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_COLLEAGUES
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestCancelledColleagues)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_INCOMING_QUOTE}
                                                                value={
                                                                    mailNotifs[UserSettingKey.EMAIL_NOTIFICATIONS_INCOMING_QUOTE]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_INCOMING_QUOTE
                                                                )}
                                                                label={t(UserI18N.notifications.label.offerReceived)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_REVISED_QUOTE_RECEIVED}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_REVISED_QUOTE_RECEIVED
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REVISED_QUOTE_RECEIVED
                                                                )}
                                                                label={t(UserI18N.notifications.label.revisedQuoteReceived)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_WITHDRAWN}
                                                                value={
                                                                    mailNotifs[UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_WITHDRAWN]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_WITHDRAWN
                                                                )}
                                                                label={t(UserI18N.notifications.label.quoteWithdrawn)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_COOP_QUOTE_CONFIRMED_ME}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_COOP_QUOTE_CONFIRMED_ME
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_COOP_QUOTE_CONFIRMED_ME
                                                                )}
                                                                label={t(UserI18N.notifications.label.offerConfirmedMe)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_COOP_QUOTE_CONFIRMED_COLLEAGUES
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_COOP_QUOTE_CONFIRMED_COLLEAGUES
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_COOP_QUOTE_CONFIRMED_COLLEAGUES
                                                                )}
                                                                label={t(UserI18N.notifications.label.offerConfirmedColleagues)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_ORDER_STATUS_CHANGED_REQUESTER
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_ORDER_STATUS_CHANGED_REQUESTER
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_ORDER_STATUS_CHANGED_REQUESTER
                                                                )}
                                                                label={t(UserI18N.notifications.label.orderStatusChangedRequester)}
                                                                dense
                                                        />
                                                    </FormGroup>
                                                </GroupBox>

                                                <GroupBox title={t(UserI18N.notifications.subgroup.automatic)}>
                                                    <FormGroup>
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_REMINDER_BEFORE_DUE
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_REQUEST_REMINDER_BEFORE_DUE
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_REMINDER_BEFORE_DUE
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestReminderBeforeDue)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_DEACTIVATED_AFTER_DUE
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_REQUEST_DEACTIVATED_AFTER_DUE
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_DEACTIVATED_AFTER_DUE
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestDeactivatedAfterDue)}
                                                                dense
                                                        />
                                                    </FormGroup>
                                                </GroupBox>
                                            </Grid>
                                        </Grid>

                                        {/* Offerer Section - Right Column */}
                                        <Grid size={{xs: 12, lg: 6}}>
                                            <Grid container direction="column" spacing={3}>
                                                <Typography variant="h6" gutterBottom>
                                                    {t(UserI18N.notifications.section.offerer)}
                                                </Typography>

                                                <GroupBox title={t(UserI18N.notifications.subgroup.eventBased)}>
                                                    <FormGroup>
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_NEW_REQUEST_PUBLISHED}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_NEW_REQUEST_PUBLISHED
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_NEW_REQUEST_PUBLISHED
                                                                )}
                                                                label={t(UserI18N.notifications.label.newRequestAvailable)}
                                                                dense
                                                        />
                                                        <Box sx={{pl: 4, pb: 1, maxWidth: 280}}>
                                                            <NumberInputField
                                                                    name={MAX_DISTANCE_KM_KEY}
                                                                    label={t(UserI18N.notifications.field.maxDistanceKm)}
                                                                    value={maxDistanceKm}
                                                                    onChange={handleMaxDistanceKmChange}
                                                                    disabled={
                                                                        !mailNotifs[
                                                                                UserSettingKey.EMAIL_NOTIFICATIONS_NEW_REQUEST_PUBLISHED
                                                                                ]
                                                                    }
                                                                    min={Constants.MAX_DISTANCE_KM_MIN}
                                                                    max={Constants.MAX_DISTANCE_KM_MAX}
                                                                    decimalScale={0}
                                                                    helperText={t(UserI18N.notifications.tooltip.maxDistanceKmHelp)}
                                                                    placeholder={t(UserI18N.notifications.tooltip.maxDistanceKmHelp)}
                                                            />
                                                        </Box>
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_INCOMING_COOP_REQUEST_CONFIRMED_ME
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_INCOMING_COOP_REQUEST_CONFIRMED_ME
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_INCOMING_COOP_REQUEST_CONFIRMED_ME
                                                                )}
                                                                label={t(UserI18N.notifications.label.incomingCoopRequestConfirmedMe)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_INCOMING_COOP_REQUEST_CONFIRMED_COLLEAGUES
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_INCOMING_COOP_REQUEST_CONFIRMED_COLLEAGUES
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_INCOMING_COOP_REQUEST_CONFIRMED_COLLEAGUES
                                                                )}
                                                                label={t(
                                                                        UserI18N.notifications.label.incomingCoopRequestConfirmedColleagues
                                                                )}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_YOUR_COOP_QUOTE_ACCEPTED}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_YOUR_COOP_QUOTE_ACCEPTED
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_YOUR_COOP_QUOTE_ACCEPTED
                                                                )}
                                                                label={t(UserI18N.notifications.label.yourCoopOfferAccepted)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_YOUR_COOP_QUOTE_DECLINED}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_YOUR_COOP_QUOTE_DECLINED
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_YOUR_COOP_QUOTE_DECLINED
                                                                )}
                                                                label={t(UserI18N.notifications.label.yourCoopOfferDeclined)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_FEEDBACK_RECEIVED}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_FEEDBACK_RECEIVED
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_FEEDBACK_RECEIVED
                                                                )}
                                                                label={t(UserI18N.notifications.label.quoteFeedbackReceived)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_DATES_EXTENDED}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_DATES_EXTENDED
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_DATES_EXTENDED
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestDatesExtended)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_ADDITIONAL_INFORMATION_UPDATED
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_REQUEST_ADDITIONAL_INFORMATION_UPDATED
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_ADDITIONAL_INFORMATION_UPDATED
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestAdditionalInformationUpdated)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_SUPPLIER}
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_SUPPLIER
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_REQUEST_CANCELLED_SUPPLIER
                                                                )}
                                                                label={t(UserI18N.notifications.label.requestCancelledSupplier)}
                                                                dense
                                                        />
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_ORDER_STATUS_CHANGED_OFFERER
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_ORDER_STATUS_CHANGED_OFFERER
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_ORDER_STATUS_CHANGED_OFFERER
                                                                )}
                                                                label={t(UserI18N.notifications.label.orderStatusChangedOfferer)}
                                                                dense
                                                        />
                                                    </FormGroup>
                                                </GroupBox>

                                                <GroupBox title={t(UserI18N.notifications.subgroup.automatic)}>
                                                    <FormGroup>
                                                        <CheckboxInputField
                                                                name={
                                                                    UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_DEACTIVATED_AFTER_DUE
                                                                }
                                                                value={
                                                                    mailNotifs[
                                                                            UserSettingKey
                                                                                    .EMAIL_NOTIFICATIONS_QUOTE_DEACTIVATED_AFTER_DUE
                                                                            ]
                                                                }
                                                                onChange={handleNotifCheckboxChange(
                                                                        UserSettingKey.EMAIL_NOTIFICATIONS_QUOTE_DEACTIVATED_AFTER_DUE
                                                                )}
                                                                label={t(UserI18N.notifications.label.offerDeactivatedAfterDue)}
                                                                dense
                                                        />
                                                    </FormGroup>
                                                </GroupBox>
                                            </Grid>
                                        </Grid>

                                        {/* Save Buttons - Horizontal at Bottom */}
                                        <Grid size={{xs: 12}}>
                                            <Grid container direction="row" spacing={2}>
                                                <Grid>
                                                    <ButtonEx
                                                            label={t(UserI18N.notifications.action.saveCurrentCompany)}
                                                            onClick={handleSaveNotifications}
                                                            color="primary"
                                                            disabled={!notifDirty || notifSaving}
                                                            data-cy="save-notifications-button"
                                                    />
                                                </Grid>
                                                {hasOtherActiveMemberships && (
                                                        <Grid>
                                                            <ButtonEx
                                                                    label={t(UserI18N.notifications.action.saveAllCompanies)}
                                                                    onClick={handleCopyEmailSettings}
                                                                    color="secondary"
                                                                    disabled={!notifDirty || notifSaving}
                                                                    startIcon={notifSaving ? <CircularProgress size={16}
                                                                                                               color="inherit"/> : undefined}
                                                                    data-cy="copy-email-settings-button"
                                                            />
                                                        </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </CardEx>
                            </Grid>
                    )}
                </Grid>
                <DialogNode/>
                <LinkDisplayDialog
                        open={linkDialogData !== undefined}
                        onClose={() => setLinkDialogData(undefined)}
                        title={linkDialogData?.title ?? ""}
                        link={linkDialogData?.url}
                        validUntil={linkDialogData?.validUntil}
                        subtitle={linkDialogData?.subtitle}
                />
            </AppFrame>
    );
};

export default withAuthWrapperComponent(UserPage);
